// src/pages/ListingsDashboard.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, addDoc, getDocs, deleteDoc, doc, updateDoc, query, orderBy, where } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, auth, storage } from '../firebaseConfig';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { v4 as uuidv4 } from 'uuid';

import './ListingsDashboard.css';

// --- Define your PRE-DEFINED contact number here ---
// const PREDEFINED_CONTACT_NUMBER = "+91 9569994536"; // <--- CHANGE THIS TO YOUR DESIRED NUMBER

const ListingsDashboard = () => {
    const [user, setUser] = useState(null);
    const [loadingAuth, setLoadingAuth] = useState(true);
    const navigate = useNavigate();

    // --- Form states for adding/editing a new listing ---
    const [listingName, setListingName] = useState('');
    const [slug, setSlug] = useState('');
    const [rentPrice, setRentPrice] = useState('');
    const [rentUnit, setRentUnit] = useState(' per month');
    const [location, setLocation] = useState('');
    const [propertyType, setPropertyType] = useState('');
    // New state for original price
    const [originalPrice, setOriginalPrice] = useState('');
    // NEW FIELD FOR URGENCY MESSAGE (free-form text)
    const [urgencyMessage, setUrgencyMessage] = useState('');
    // NEW FIELD FOR GENDER PREFERENCE
    const [genderPreference, setGenderPreference] = useState('Co-Living');

    // NEW FIELDS: BHK/RK/Sharing - now split into two
    const [bhkRkNumber, setBhkRkNumber] = useState(''); // For the number part
    const [bhkRkUnit, setBhkRkUnit] = useState('');     // For the unit part (BHK, RK, Sharing, etc.)

    // NEW FIELD: Nearby Important Places
    const [nearbyPlaces, setNearbyPlaces] = useState('');

    // State for image handling: can be URL OR File object
    const [featuredImageUrl, setFeaturedImageUrl] = useState(''); // For manual URL input
    const [featuredImageFile, setFeaturedImageFile] = useState(null); // For local file upload
    const [featuredImagePreview, setFeaturedImagePreview] = useState(null); // For local file preview

    const [galleryImageUrls, setGalleryImageUrls] = useState(''); // For comma-separated URLs
    const [galleryImageFiles, setGalleryImageFiles] = useState([]); // Array of File objects for local uploads
    const [galleryImagesPreviews, setGalleryImagesPreviews] = useState([]); // Array of URLs for local file previews

    // --- Video Handling States ---
    const [videoUrls, setVideoUrls] = useState(''); // For comma-separated video URLs
    const [videoFiles, setVideoFiles] = useState([]); // Array of File objects for local video uploads
    const [videoPreviews, setVideoPreviews] = useState([]); // Array of URLs for local video previews

    const [description, setDescription] = useState('');
    const [amenities, setAmenities] = useState('');
    const [availableFrom, setAvailableFrom] = useState('');

    const [formError, setFormError] = useState('');
    const [formSuccess, setFormSuccess] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);

    // --- States for viewing/editing existing listings ---
    const [listings, setListings] = useState([]);
    const [loadingListings, setLoadingListings] = useState(true);
    const [editMode, setEditMode] = useState(false);
    const [currentListingId, setCurrentListingId] = useState(null);

    // --- Authentication Check (runs once on component mount) ---
    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
            if (currentUser) {
                setUser(currentUser);
                fetchListings();
            } else {
                navigate('/listingslogin');
            }
            setLoadingAuth(false);
        });
        return () => unsubscribe();
    }, [navigate]);

    // --- Effect for Featured Image Preview cleanup ---
    useEffect(() => {
        return () => {
            if (featuredImagePreview) {
                URL.revokeObjectURL(featuredImagePreview);
            }
        };
    }, [featuredImagePreview]);

    // --- Effect for Gallery Images Preview cleanup ---
    useEffect(() => {
        return () => {
            galleryImagesPreviews.forEach(url => URL.revokeObjectURL(url));
        };
    }, [galleryImagesPreviews]);

    // --- Effect for Video Previews cleanup ---
    useEffect(() => {
        return () => {
            videoPreviews.forEach(url => URL.revokeObjectURL(url));
        };
    }, [videoPreviews]);


    // --- Fetch Listings from Firestore ---
    const fetchListings = async () => {
        setLoadingListings(true);
        setFormError('');
        try {
            const q = query(collection(db, 'website_listings'), orderBy('createdAt', 'desc'));
            const querySnapshot = await getDocs(q);
            const fetchedListings = querySnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));
            setListings(fetchedListings);
        } catch (error) {
            console.error("Error fetching listings:", error);
            setFormError("Failed to load existing listings.");
        } finally {
            setLoadingListings(false);
        }
    };

    // --- Helper function to generate a slug from a string ---
    const generateSlug = (text) => {
        return text
            .toString()
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
            .toLowerCase()
            .trim()
            .replace(/\s+/g, '-')
            .replace(/[^\w-]+/g, '')
            .replace(/--+/g, '-')
            .replace(/^-+/, '')
            .replace(/-+$/, '');
    };

    // --- Helper function to upload a file (image/video) to Firebase Storage ---
    const uploadFileAndGetURL = async (file, pathPrefix = 'listing_files') => {
        if (!file) return null;

        const uniqueFileName = `${uuidv4()}_${file.name}`;
        const storageRef = ref(storage, `${pathPrefix}/${uniqueFileName}`);

        try {
            await uploadBytes(storageRef, file);
            const url = await getDownloadURL(storageRef);
            return {
                fileName: file.name,
                storagePath: `${pathPrefix}/${uniqueFileName}`,
                file: { url: url }, // Structure to be consistent
                description: file.name,
                type: file.type.startsWith('image') ? 'image' : (file.type.startsWith('video') ? 'video' : 'other')
            };
        } catch (error) {
            console.error("Error uploading file:", file.name, error);
            throw new Error(`Failed to upload file "${file.name}".`);
        }
    };

    // --- Helper function to delete a file from Firebase Storage ---
    const deleteFileFromStorage = async (storagePath) => {
        if (!storagePath) return;
        try {
            const fileRef = ref(storage, storagePath);
            await deleteObject(fileRef);
            console.log(`File at ${storagePath} deleted from Storage.`);
        } catch (error) {
            console.warn(`Could not delete file from Storage: ${storagePath}`, error);
        }
    };

    // --- Handle Form Submission (Add or Update Listing) ---
    const handleSubmit = async (e) => {
        e.preventDefault();
        setFormError('');
        setFormSuccess('');
        setIsSubmitting(true);
        setUploadProgress(0);


        // --- Basic Client-Side Validation ---
        if (!listingName || !rentPrice || !location || !propertyType || !description || !availableFrom) {
            setFormError('All fields marked with * are required.');
            setIsSubmitting(false);
            return;
        }
        if (isNaN(parseFloat(rentPrice)) || parseFloat(rentPrice) <= 0) {
            setFormError('Rent price must be a positive number.');
            setIsSubmitting(false);
            return;
        }
        // Validate originalPrice if provided
        if (originalPrice && (isNaN(parseFloat(originalPrice)) || parseFloat(originalPrice) <= 0)) {
            setFormError('Original price must be a positive number if provided.');
            setIsSubmitting(false);
            return;
        }
        // Ensure originalPrice is greater than rentPrice if both are present
        if (originalPrice && parseFloat(originalPrice) <= parseFloat(rentPrice)) {
            setFormError('Original price must be greater than the discounted rent price.');
            setIsSubmitting(false);
            return;
        }


        if (!featuredImageFile && !featuredImageUrl) {
            setFormError('Either a featured image file or URL is required.');
            setIsSubmitting(false);
            return;
        }
        if (featuredImageUrl) {
            try { new URL(featuredImageUrl); } catch (_) { setFormError('Featured image URL is invalid.'); setIsSubmitting(false); return; }
        }
        if (galleryImageUrls) {
            const urls = galleryImageUrls.split(',').map(url => url.trim()).filter(url => url !== '');
            for (const url of urls) {
                try { new URL(url); } catch (_) { setFormError('One or more gallery image URLs are invalid.'); setIsSubmitting(false); return; }
            }
        }
        // Video URL Validation
        if (videoUrls) {
            const urls = videoUrls.split(',').map(url => url.trim()).filter(url => url !== '');
            for (const url of urls) {
                try { new URL(url); } catch (_) { setFormError('One or more video URLs are invalid.'); setIsSubmitting(false); return; }
            }
        }

        // --- START UNIQUE SLUG GENERATION LOGIC ---
        let baseSlug = generateSlug(listingName);
        let finalSlug = baseSlug;
        let slugCounter = 1;
        let isSlugUnique = false;

        // The current listing being edited (if in edit mode)
        const currentListingIdOnEdit = editMode ? currentListingId : null;

        // Always perform the uniqueness check
        while (!isSlugUnique) {
            const q = query(collection(db, 'website_listings'), where('slug', '==', finalSlug));
            const querySnapshot = await getDocs(q);

            // Check if the slug is truly unique OR if the only document found with this slug
            // is the *current* listing being edited.
            if (querySnapshot.empty || (querySnapshot.docs.length === 1 && querySnapshot.docs[0].id === currentListingIdOnEdit)) {
                isSlugUnique = true;
            } else {
                // Slug is not unique and is not the current listing being edited, increment counter and try a new slug
                slugCounter++;
                finalSlug = `${baseSlug}-${slugCounter}`;
            }
        }
        // --- END UNIQUE SLUG GENERATION LOGIC ---


        try {
            let finalFeaturedImageData = null;
            const oldListingDoc = listings.find(l => l.id === currentListingId);

            if (featuredImageFile) {
                // Scenario 1: New file is uploaded by the user
                finalFeaturedImageData = await uploadFileAndGetURL(featuredImageFile, 'listing_media/featured');
            } else if (featuredImageUrl) {
                // Scenario 2: User provided a URL (could be the old one, or a new external URL)
                finalFeaturedImageData = {
                    file: { url: featuredImageUrl },
                    description: listingName,
                    fileName: 'external_url_image', // Default, will be updated if it's an old uploaded file
                    storagePath: null, // Default, will be updated if it's an old uploaded file
                    type: 'image' // Assume image for external URL unless specified
                };

                // IMPORTANT MODIFICATION: This entire 'if' block was added/modified
                // If we are in edit mode, and the current featuredImageUrl matches the URL
                // of the old featured image AND the old image had a storagePath (meaning it was an uploaded file),
                // then preserve the original storagePath, filename, and type.
                if (editMode && oldListingDoc?.featuredImage?.file?.url === featuredImageUrl && oldListingDoc?.featuredImage?.storagePath) {
                    finalFeaturedImageData.storagePath = oldListingDoc.featuredImage.storagePath;
                    finalFeaturedImageData.fileName = oldListingDoc.featuredImage.fileName;
                    finalFeaturedImageData.type = oldListingDoc.featuredImage.type; // Preserve original type too
                }
            }

            const finalGalleryImagesData = [];
            if (galleryImageFiles.length > 0) {
                for (let i = 0; i < galleryImageFiles.length; i++) {
                    const file = galleryImageFiles[i];
                    // Adjusted upload progress calculation to account for all files
                    const totalFilesToUpload = (featuredImageFile ? 1 : 0) + galleryImageFiles.length + videoFiles.length;
                    let uploadedCount = (featuredImageFile ? 1 : 0) + i; // Count already processed files
                    setUploadProgress(Math.round((uploadedCount / totalFilesToUpload) * 100));

                    const imageData = await uploadFileAndGetURL(file, 'listing_media/gallery_images');
                    finalGalleryImagesData.push(imageData);
                }
            } else if (galleryImageUrls) {
                galleryImageUrls.split(',').map(url => url.trim()).filter(url => url !== '').forEach(url => {
                    finalGalleryImagesData.push({
                        file: { url: url },
                        description: `${listingName} gallery image`,
                        fileName: 'external_url_image',
                        storagePath: null,
                        type: 'image'
                    });
                });
            }

            // Handle Video Uploads - NO POSTER URL GENERATION OR STORAGE
            const finalVideoData = [];
            if (videoFiles.length > 0) {
                for (let i = 0; i < videoFiles.length; i++) {
                    const file = videoFiles[i];
                    // Adjust progress calculation to include images and videos
                    const totalFilesToUpload = (featuredImageFile ? 1 : 0) + galleryImageFiles.length + videoFiles.length;
                    let uploadedCount = (featuredImageFile ? 1 : 0) + galleryImageFiles.length + i; // Count already processed files
                    setUploadProgress(Math.round((uploadedCount / totalFilesToUpload) * 100));

                    const videoData = await uploadFileAndGetURL(file, 'listing_media/videos');
                    // Ensure type is 'video' and posterUrl is NOT set
                    finalVideoData.push({ ...videoData, type: 'video' });
                }
            } else if (videoUrls) {
                videoUrls.split(',').map(url => url.trim()).filter(url => url !== '').forEach(url => {
                    finalVideoData.push({
                        file: { url: url },
                        description: `${listingName} video`,
                        fileName: 'external_url_video',
                        storagePath: null,
                        type: 'video' // Explicitly set type for external URLs too
                    });
                });
            }
            // Final progress update after all files are theoretically uploaded
            setUploadProgress(90);

            // Construct the bhkRkSharing string
            let finalBhkRkSharing = '';
            if (bhkRkNumber && bhkRkUnit) {
                finalBhkRkSharing = `${bhkRkNumber}${bhkRkUnit}`;
            } else if (bhkRkUnit && !bhkRkNumber && bhkRkUnit.toLowerCase().includes('sharing')) {
                // If it's a sharing unit, and no number, just use the unit (e.g., "Single Sharing")
                finalBhkRkSharing = bhkRkUnit;
            } else if (bhkRkNumber && !bhkRkUnit) {
                // If only number is given, save it as just the number (unlikely, but safe)
                finalBhkRkSharing = bhkRkNumber;
            }


            const listingData = {
                listingName,
                slug: finalSlug,
                rentPrice: parseFloat(rentPrice),
                rentUnit,
                location,
                propertyType,
                featuredImage: finalFeaturedImageData,
                galleryImages: finalGalleryImagesData,
                videos: finalVideoData, // Storing videos without posterUrl
                // NEW FIELDS:
                urgencyMessage: urgencyMessage, // Save the custom urgency message
                genderPreference: genderPreference,
                bhkRkSharing: finalBhkRkSharing, // Save the combined BHK/RK/Sharing string
                nearbyPlaces: nearbyPlaces.split(',').map(item => item.trim()).filter(item => item !== ''), // Save nearby places as an array
                description,
                amenities: amenities.split(',').map(item => item.trim()).filter(item => item !== ''),
                availableFrom: new Date(availableFrom),
                createdAt: editMode ? listings.find(l => l.id === currentListingId)?.createdAt || new Date() : new Date(),
                updatedAt: new Date()
            };

            // Add originalPrice to listingData if it's provided and valid
            if (originalPrice && parseFloat(originalPrice) > parseFloat(rentPrice)) {
                listingData.originalPrice = parseFloat(originalPrice);
            } else if (editMode && currentListingId) {
                // If in edit mode and originalPrice is cleared or invalid, remove it from the document
                listingData.originalPrice = null; // Set to null to remove from Firestore
            }

            if (editMode && currentListingId) {
                const docRef = doc(db, 'website_listings', currentListingId);
                const oldListingDoc = listings.find(l => l.id === currentListingId);

                // The condition below remains the same, but now functions correctly
                // because finalFeaturedImageData.storagePath is correctly set above.
                if (oldListingDoc?.featuredImage?.storagePath && oldListingDoc.featuredImage.storagePath !== listingData.featuredImage?.storagePath) {
                    await deleteFileFromStorage(oldListingDoc.featuredImage.storagePath);
                }
                // Handle gallery images update/deletion
                // This logic is for when new files are uploaded, replacing old ones.
                // If files are provided via 'files' input, we assume they replace the old ones.
                // If URLs are provided, we assume old stored files are no longer needed.
                if (galleryImageFiles.length > 0 && oldListingDoc?.galleryImages) {
                    for (const oldImg of oldListingDoc.galleryImages) {
                        if (oldImg.storagePath) {
                            await deleteFileFromStorage(oldImg.storagePath);
                        }
                    }
                }
                // Handle video update/deletion - same logic as gallery images
                if (videoFiles.length > 0 && oldListingDoc?.videos) {
                    for (const oldVideo of oldListingDoc.videos) {
                        if (oldVideo.storagePath) {
                            await deleteFileFromStorage(oldVideo.storagePath);
                        }
                    }
                }

                await updateDoc(docRef, listingData);
                setFormSuccess('Listing updated successfully!');
            } else {
                await addDoc(collection(db, 'website_listings'), listingData);
                setFormSuccess('Listing added successfully!');
            }
            resetForm();
            fetchListings();
        } catch (error) {
            console.error("Error saving listing:", error);
            setFormError(`Failed to save listing: ${error.message || 'Unknown error'}`);
        } finally {
            setIsSubmitting(false);
            setUploadProgress(0); // Ensure progress bar hides on completion/error
        }
    };

    // --- Reset Form Fields to initial empty state ---
    const resetForm = () => {
        setListingName('');
        setSlug('');
        setRentPrice('');
        setRentUnit(' per month');
        setLocation('');
        setPropertyType('');
        setOriginalPrice(''); // Reset original price
        setFeaturedImageUrl('');
        setFeaturedImageFile(null);
        setFeaturedImagePreview(null);
        setGalleryImageUrls('');
        setGalleryImageFiles([]);
        setGalleryImagesPreviews([]);
        setVideoUrls('');
        setVideoFiles([]);
        setVideoPreviews([]);
        // Reset NEW FIELDS
        setUrgencyMessage(''); // Reset urgency message
        setGenderPreference('Co-Living');
        setBhkRkNumber(''); // Reset BHK/RK number
        setBhkRkUnit('');   // Reset BHK/RK unit
        setNearbyPlaces(''); // Reset Nearby Important Places
        setDescription('');
        setAmenities('');
        setAvailableFrom('');
        setEditMode(false);
        setCurrentListingId(null);
        setFormError('');
        setFormSuccess('');
        setUploadProgress(0);
    };

    // --- Handle Edit Button Click (populates form for editing) ---
    const handleEdit = (listing) => {
        setListingName(listing.listingName);
        setSlug(listing.slug);
        setRentPrice(listing.rentPrice.toString());
        setRentUnit(listing.rentUnit);
        setLocation(listing.location);
        setPropertyType(listing.propertyType);
        setOriginalPrice(listing.originalPrice?.toString() || ''); // Populate original price

        // Populate URL fields, clear file inputs and previews
        setFeaturedImageUrl(listing.featuredImage?.file?.url || '');
        setFeaturedImageFile(null);
        setFeaturedImagePreview(null);

        setGalleryImageUrls(listing.galleryImages?.map(img => img.file?.url).filter(Boolean).join(', ') || '');
        setGalleryImageFiles([]);
        setGalleryImagesPreviews([]);

        // Populate video states for editing (only URLs, no posterUrl)
        setVideoUrls(listing.videos?.map(video => video.file?.url).filter(Boolean).join(', ') || '');
        setVideoFiles([]);
        setVideoPreviews([]);

        // Populate NEW FIELDS for editing
        setUrgencyMessage(listing.urgencyMessage || ''); // Populate urgency message
        setGenderPreference(listing.genderPreference || 'Co-Living'); // Default to 'Co-Living' if undefined

        // Parse bhkRkSharing back into number and unit for editing
        const config = listing.bhkRkSharing || '';
        const match = config.match(/^(\d+)?\s*([A-Za-z\s]+)?$/); // Matches "1BHK", "2 RK", "Triple Sharing"
        if (match) {
            setBhkRkNumber(match[1] || ''); // The number part, if it exists
            const unitPart = (match[2] || '').trim();
            // Check if unitPart is a full sharing string, otherwise append it to the number
            if (['bhk', 'rk'].includes(unitPart.toLowerCase())) {
                setBhkRkUnit(unitPart.toUpperCase());
            } else if (unitPart.toLowerCase().includes('sharing')) {
                setBhkRkUnit(unitPart); // Keep the full sharing string as the unit
            } else if (unitPart === '') {
                setBhkRkUnit('');
            } else {
                // Fallback for cases like "1 Bed", which might just be number
                setBhkRkUnit(unitPart); // Store as is if it doesn't fit standard pattern
            }
        } else {
            setBhkRkNumber('');
            setBhkRkUnit('');
        }
        
        setNearbyPlaces(listing.nearbyPlaces?.join(', ') || ''); // Populate nearby places

        setDescription(listing.description);
        setAmenities(listing.amenities?.join(', ') || '');
        if (listing.availableFrom && listing.availableFrom.toDate) {
            const date = listing.availableFrom.toDate();
            setAvailableFrom(date.toISOString().split('T')[0]);
        } else {
            setAvailableFrom('');
        }
        setEditMode(true);
        setCurrentListingId(listing.id);
        setFormError('');
        setFormSuccess('');
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    // --- Handle Delete Button Click ---
    const handleDelete = async (id) => {
        if (window.confirm('Are you sure you want to delete this listing permanently? This will also attempt to delete associated images and videos from storage.')) {
            try {
                const listingToDelete = listings.find(l => l.id === id);

                if (listingToDelete?.featuredImage?.storagePath) {
                    await deleteFileFromStorage(listingToDelete.featuredImage.storagePath);
                }

                if (listingToDelete?.galleryImages && Array.isArray(listingToDelete.galleryImages)) {
                    for (const img of listingToDelete.galleryImages) {
                        if (img.storagePath) {
                            await deleteFileFromStorage(img.storagePath);
                        }
                    }
                }

                // Delete associated videos from storage
                if (listingToDelete?.videos && Array.isArray(listingToDelete.videos)) {
                    for (const video of listingToDelete.videos) {
                        if (video.storagePath) {
                            await deleteFileFromStorage(video.storagePath);
                        }
                    }
                }

                await deleteDoc(doc(db, 'website_listings', id));
                setFormSuccess('Listing and associated media deleted successfully!');
                fetchListings();
            } catch (error) {
                console.error("Error deleting listing or media:", error);
                setFormError('Failed to delete listing or some associated media. Check console for details.');
            }
        }
    };

    // --- Handle Logout ---
    const handleLogout = async () => {
        try {
            await signOut(auth);
            navigate('/listingslogin');
        } catch (error) {
            console.error("Error logging out:", error);
            setFormError('Failed to log out.');
        }
    };

    if (loadingAuth) {
        return <div className="listings-dashboard-container page-fade-in">Authenticating...</div>;
    }

    if (!user) {
        return null;
    }

    return (
        <div className="listings-dashboard-container page-fade-in">
            <div className="listings-header">
                <h1>RoomEase Listings Dashboard</h1>
                <button onClick={handleLogout} className="logout-button">Logout</button>
            </div>

            <div className="listings-content">

                {/* Existing Listings Display Section */}
                <div className="existing-listings-section">
                    <h2>Existing Listings</h2>
                    {loadingListings ? (
                        <p>Loading listings...</p>
                    ) : listings.length === 0 ? (
                        <p>No listings added yet. Use the form on the left to add your first one!</p>
                    ) : (
                        <ul className="listings-list">
                            {listings.map(listing => (
                                <li key={listing.id} className="listing-item">
                                    <div className="listing-info">
                                        <strong>{listing.listingName}</strong><br />
                                        <span className="listing-location-text">{listing.location}</span> - ₹{listing.rentPrice} {listing.rentUnit}
                                        {/* Display original price if exists for dashboard clarity */}
                                        {listing.originalPrice && listing.originalPrice > listing.rentPrice && (
                                            <span style={{ marginLeft: '10px', color: 'gray', textDecoration: 'line-through' }}>
                                                ₹{listing.originalPrice}
                                            </span>
                                        )}
                                        {/* <p>Contact: {PREDEFINED_CONTACT_NUMBER}</p> */}
                                    </div>
                                    <div className="listing-actions">
                                        <button onClick={() => handleEdit(listing)} className="edit-button">Edit</button>
                                        <button onClick={() => handleDelete(listing.id)} className="delete-button">Delete</button>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>

                {/* Listing Creation/Edit Form Section */}
                <div className="listing-form-section">
                    <h2>{editMode ? 'Edit Listing' : 'Add New Listing'}</h2>
                    <form onSubmit={handleSubmit} className="listing-form">
                        <div className="form-group">
                            <label htmlFor="listingName">Listing Name: <span className="required-asterisk">*</span></label>
                            <input
                                type="text"
                                id="listingName"
                                value={listingName}
                                onChange={(e) => {
                                    setListingName(e.target.value);
                                    setSlug(generateSlug(e.target.value));
                                }}
                                required
                            />
                        </div>

                        <div className="form-group">
                            <label htmlFor="slug">Auto-Generated Slug:</label>
                            <input type="text" id="slug" value={slug} readOnly disabled />
                            <small>This is automatically generated from the Listing Name.</small>
                        </div>

                        <div className="form-group-inline">
                            <div className="form-group">
                                <label htmlFor="rentPrice">Rent Price: <span className="required-asterisk">*</span></label>
                                <input type="number" id="rentPrice" value={rentPrice} onChange={(e) => setRentPrice(e.target.value)} required min="1" />
                            </div>
                            <div className="form-group">
                                <label htmlFor="rentUnit">Rent Unit:</label>
                                <select id="rentUnit" value={rentUnit} onChange={(e) => setRentUnit(e.target.value)}>
                                    <option value=" per head"> per head</option>
                                    <option value=" per month"> per month</option>
                                    <option value=" per year"> per year</option>
                                    <option value=" per month per head"> per month per head</option>
                                    <option value=" per annum per head"> per annum per head</option>
                                </select>
                            </div>
                        </div>

                        {/* New input for Original Price */}
                        <div className="form-group">
                            <label htmlFor="originalPrice">Original Price (optional, for discounts):</label>
                            <input
                                type="number"
                                id="originalPrice"
                                value={originalPrice}
                                onChange={(e) => setOriginalPrice(e.target.value)}
                                min="1"
                                placeholder="e.g., 12000"
                            />
                            <small>If set and higher than Rent Price, it will be displayed as a discounted price.</small>
                        </div>


                        <div className="form-group">
                            <label htmlFor="location">Location (e.g., Koregaon Park, Pune): <span className="required-asterisk">*</span></label>
                            <input type="text" id="location" value={location} onChange={(e) => setLocation(e.target.value)} required />
                        </div>

                        <div className="form-group">
                            <label htmlFor="propertyType">Property Type: <span className="required-asterisk">*</span></label>
                            <select id="propertyType" value={propertyType} onChange={(e) => setPropertyType(e.target.value)} required>
                                <option value="">Select Type</option>
                                <option value="Flat">Flat</option>
                                <option value="PG">PG</option>
                                <option value="Villa">Villa</option>
                                <option value="Hostel">Hostel</option>
                            </select>
                        </div>

                        {/* --- Featured Image Section --- */}
                        <div className="form-group image-upload-section">
                            <label>Featured Image: <span className="required-asterisk">*</span></label>
                            <div className="image-input-group">
                                <label htmlFor="featuredImageFile" className="file-input-label">Upload File</label>
                                <input
                                    type="file"
                                    id="featuredImageFile"
                                    accept="image/*"
                                    onChange={(e) => {
                                        const file = e.target.files[0];
                                        setFeaturedImageFile(file);
                                        setFeaturedImageUrl('');
                                        if (file) {
                                            setFeaturedImagePreview(URL.createObjectURL(file));
                                        } else {
                                            setFeaturedImagePreview(null);
                                        }
                                    }}
                                    style={{ display: 'none' }}
                                />
                                {featuredImageFile && <p className="selected-file-name">Selected: {featuredImageFile.name}</p>}
                                {featuredImagePreview && (
                                    <div className="image-preview">
                                        <img src={featuredImagePreview} alt="Featured Preview" />
                                        <small>Local file preview</small>
                                    </div>
                                )}

                                <span className="or-separator">OR</span>
                                <input
                                    type="url"
                                    id="featuredImageUrl"
                                    value={featuredImageUrl}
                                    onChange={(e) => {
                                        setFeaturedImageUrl(e.target.value);
                                        setFeaturedImageFile(null);
                                        setFeaturedImagePreview(null);
                                    }}
                                    placeholder="Enter image URL"
                                    className="url-input"
                                />
                                <small className="input-hint">Upload a file or provide a direct URL for the main image.</small>
                            </div>
                            {editMode && featuredImageUrl && !featuredImageFile && (
                                <div className="current-image-preview">
                                    <p>Current Featured Image:</p>
                                    <img src={featuredImageUrl} alt="Current Featured" style={{ maxWidth: '100px', maxHeight: '100px', objectFit: 'cover' }} />
                                    <small>(This is the existing remote image)</small>
                                </div>
                            )}
                        </div>

                        {/* --- Gallery Images Section --- */}
                        <div className="form-group image-upload-section">
                            <label>Gallery Images:</label>
                            <div className="image-input-group">
                                <label htmlFor="galleryImageFiles" className="file-input-label">Upload Files</label>
                                <input
                                    type="file"
                                    id="galleryImageFiles"
                                    accept="image/*"
                                    multiple
                                    onChange={(e) => {
                                        const files = Array.from(e.target.files);
                                        setGalleryImageFiles(files);
                                        setGalleryImageUrls('');

                                        const newPreviews = files.map(file => URL.createObjectURL(file));
                                        setGalleryImagesPreviews(newPreviews);
                                    }}
                                    style={{ display: 'none' }}
                                />
                                {galleryImageFiles.length > 0 && (
                                    <p className="selected-file-name">Selected: {galleryImageFiles.map(f => f.name).join(', ')}</p>
                                )}
                                {galleryImagesPreviews.length > 0 && (
                                    <div className="image-preview-gallery">
                                        {galleryImagesPreviews.map((src, index) => (
                                            <div key={index} className="image-preview-item">
                                                <img src={src} alt={`Gallery Preview ${index + 1}`} />
                                                <small>Local</small>
                                            </div>
                                        ))}
                                    </div>
                                )}

                                <span className="or-separator">OR</span>
                                <textarea
                                    id="galleryImageUrls"
                                    rows="3"
                                    value={galleryImageUrls}
                                    onChange={(e) => {
                                        setGalleryImageUrls(e.target.value);
                                        setGalleryImageFiles([]);
                                        setGalleryImagesPreviews([]);
                                    }}
                                    placeholder="https://example.com/img1.jpg, https://example.com/img2.jpg"
                                    className="url-input"
                                ></textarea>
                                <small className="input-hint">Upload multiple files or provide comma-separated URLs.</small>
                            </div>
                            {editMode && galleryImageUrls && galleryImageUrls.split(',').filter(Boolean).length > 0 && !galleryImageFiles.length > 0 && (
                                <div className="current-image-preview-gallery">
                                    <p>Current Gallery Images:</p>
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px' }}>
                                        {galleryImageUrls.split(',').filter(Boolean).map((url, index) => (
                                            <div key={index} className="image-preview-item">
                                                <img src={url.trim()} alt={`Current Gallery ${index}`} style={{ maxWidth: '70px', maxHeight: '70px', objectFit: 'cover' }} />
                                                <small>Remote</small>
                                            </div>
                                        ))}
                                    </div>
                                    <small>(These are existing remote images)</small>
                                </div>
                            )}
                        </div>

                        {/* --- Video Section --- */}
                        <div className="form-group video-upload-section">
                            <label>Videos:</label>
                            <div className="video-input-group">
                                <label htmlFor="videoFiles" className="file-input-label">Upload Video Files</label>
                                <input
                                    type="file"
                                    id="videoFiles"
                                    accept="video/*"
                                    multiple
                                    onChange={(e) => {
                                        const files = Array.from(e.target.files);
                                        setVideoFiles(files);
                                        setVideoUrls('');

                                        const newPreviews = files.map(file => URL.createObjectURL(file));
                                        setVideoPreviews(newPreviews);
                                    }}
                                    style={{ display: 'none' }}
                                />
                                {videoFiles.length > 0 && (
                                    <p className="selected-file-name">Selected: {videoFiles.map(f => f.name).join(', ')}</p>
                                )}
                                {videoPreviews.length > 0 && (
                                    <div className="video-preview-gallery">
                                        {videoPreviews.map((src, index) => (
                                            <div key={index} className="video-preview-item">
                                                <video src={src} controls muted style={{ maxWidth: '80px', maxHeight: '80px', objectFit: 'cover' }} />
                                                <small>Local</small>
                                            </div>
                                        ))}
                                    </div>
                                )}

                                <span className="or-separator">OR</span>
                                <textarea
                                    id="videoUrls"
                                    rows="3"
                                    value={videoUrls}
                                    onChange={(e) => {
                                        setVideoUrls(e.target.value);
                                        setVideoFiles([]);
                                        setVideoPreviews([]);
                                    }}
                                    placeholder="https://example.com/video1.mp4, https://example.com/video2.mov"
                                    className="url-input"
                                ></textarea>
                                <small className="input-hint">Upload multiple video files or provide comma-separated URLs.</small>
                            </div>
                            {editMode && videoUrls && videoUrls.split(',').filter(Boolean).length > 0 && !videoFiles.length > 0 && (
                                <div className="current-video-preview-gallery">
                                    <p>Current Videos:</p>
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px' }}>
                                        {videoUrls.split(',').filter(Boolean).map((url, index) => (
                                            <div key={index} className="video-preview-item">
                                                <video src={url.trim()} controls muted style={{ maxWidth: '70px', maxHeight: '70px', objectFit: 'cover' }} />
                                                <small>Remote</small>
                                            </div>
                                        ))}
                                    </div>
                                    <small>(These are existing remote videos)</small>
                                </div>
                            )}
                        </div>

                        <div className="form-group">
                            <label htmlFor="description">Description: <span className="required-asterisk">*</span></label>
                            <textarea id="description" rows="5" value={description} onChange={(e) => setDescription(e.target.value)} required></textarea>
                            <small>Detailed description of the property.</small>
                        </div>

                        <div className="form-group">
                            <label htmlFor="amenities">Amenities (comma-separated):</label>
                            <input type="text" id="amenities" value={amenities} onChange={(e) => setAmenities(e.target.value)} placeholder="AC, Wi-Fi, Parking, Balcony" />
                            <small>List key amenities, separated by commas.</small>
                        </div>

                        {/* Informational display for the hardcoded number */}
                        {/* <div className="form-group">
                            <label>Contact Number:</label>
                            <p className="predefined-contact-display">{PREDEFINED_CONTACT_NUMBER}</p>
                            <small>This is a predefined number and cannot be changed per listing from here.</small>
                        </div> */}


                        <div className="form-group">
                            <label htmlFor="availableFrom">Available From: <span className="required-asterisk">*</span></label>
                            <input type="date" id="availableFrom" value={availableFrom} onChange={(e) => setAvailableFrom(e.target.value)} required />
                        </div>

                        {/* NEW FIELD: Urgency Message */}
                        <div className="form-group">
                            <label htmlFor="urgencyMessage">Urgency Message (Optional):</label>
                            <input type="text" id="urgencyMessage" value={urgencyMessage} onChange={(e) => setUrgencyMessage(e.target.value)} placeholder="e.g., Book fast, filling up!" />
                        </div>

                        {/* NEW FIELD: Gender Preference */}
                        <div className="form-group">
                            <label htmlFor="genderPreference">Gender Preference:</label>
                            <select id="genderPreference" value={genderPreference} onChange={(e) => setGenderPreference(e.target.value)}>
                                <option value="">Select...</option>
                                <option value="Co-Living">Co-Living</option>
                                <option value="Girls Only">Girls Only</option>
                                <option value="Boys Only">Boys Only</option>
                            </select>
                        </div>

                        {/* NEW FIELDS: BHK/RK/Sharing - now split into two inputs */}
                        <div className="form-group-inline">
                            <div className="form-group">
                                <label htmlFor="bhkRkNumber">Config Number (Optional):</label>
                                <input
                                    type="number"
                                    id="bhkRkNumber"
                                    value={bhkRkNumber}
                                    onChange={(e) => setBhkRkNumber(e.target.value)}
                                    placeholder="e.g., 1, 2, 3"
                                    min="0"
                                />
                            </div>
                            <div className="form-group">
                                <label htmlFor="bhkRkUnit">Configuration Unit (Optional):</label>
                                <select
                                    id="bhkRkUnit"
                                    value={bhkRkUnit}
                                    onChange={(e) => setBhkRkUnit(e.target.value)}
                                >
                                    <option value="">Select Unit</option>
                                    <option value="BHK">BHK</option>
                                    <option value="RK">RK</option>
                                    <option value="Sharing">Sharing</option>
                                </select>
                            </div>
                        </div>
                        {/* <small>Specify the configuration (e.g., "1 BHK", "2 RK", "Twin Sharing").</small> */}


                        {/* NEW FIELD: Nearby Important Places (remains text input) */}
                        <div className="form-group">
                            <label htmlFor="nearbyPlaces">Nearby Important Places (Optional - comma-separated):</label>
                            <input
                                type="text"
                                id="nearbyPlaces"
                                value={nearbyPlaces}
                                onChange={(e) => setNearbyPlaces(e.target.value)}
                                placeholder="e.g., 2 mins walk from NMIMS, Near Phoenix Market City"
                            />
                            <small>List important landmarks or places nearby, separated by commas.</small>
                        </div>

                        {formError && <p className="error-message">{formError}</p>}
                        {formSuccess && <p className="success-message">{formSuccess}</p>}

                        {isSubmitting && uploadProgress > 0 && uploadProgress < 100 && (
                            <div className="upload-progress-bar-container">
                                <div className="upload-progress-bar" style={{ width: `${uploadProgress}%` }}></div>
                                <span className="upload-progress-text">Uploading media: {uploadProgress}%</span>
                            </div>
                        )}

                        <button type="submit" disabled={isSubmitting}>
                            {isSubmitting ? 'Saving...' : (editMode ? 'Update Listing' : 'Add Listing')}
                        </button>
                        {editMode && (
                            <button type="button" onClick={resetForm} className="cancel-button">
                                Cancel Edit
                            </button>
                        )}
                    </form>
                </div>
            </div>
        </div>
    );
};

export default ListingsDashboard;